// datatype=6(MASTER) の code/name 補完を共通化します。
export function createMasterNameHydratorService(deps) {
    const {
        CELLTYPE,
        fnc,
        entrydataRef,
        masterNameHydrationPendingRef,
        isEmpty,
        DicVal,
        normalizeColumnIdUpper,
        normalizeKakousijiUiString,
        readRowValueIgnoreCase,
        extractMasterCodeFromDisplayValue,
        extractMasterNameFromDisplayValue,
        resolvePanelMasterColumnDefs,
        getGridRows,
        setGridRows,
        isGridReady,
        applyGridRowsNow,
        getGridList
    } = deps;

    function normalizeText(value) {
        return normalizeKakousijiUiString(value);
    }

    function resolveFieldId(colDef) {
        const colInfo = DicVal(colDef, "colinfo", {});
        return String(
            DicVal(colInfo, "columnid",
                DicVal(colDef, "colId",
                    DicVal(colDef, "field", "")
                )
            ) ?? ""
        ).trim();
    }

    function resolveDatatype(colDef) {
        const colInfo = DicVal(colDef, "colinfo", {});
        return String(DicVal(colInfo, "datatype", DicVal(colDef, "datatype", "")) ?? "").trim();
    }

    function resolveDataId(colDef) {
        const colInfo = DicVal(colDef, "colinfo", {});
        return String(
            DicVal(colInfo, "dataid",
                DicVal(colInfo, "dataidctrl",
                    DicVal(colInfo, "mastertype",
                        DicVal(colDef, "dataid",
                            DicVal(colDef, "dataidctrl", DicVal(colDef, "mastertype", ""))
                        )
                    )
                )
            ) ?? ""
        ).trim();
    }

    function resolveDbColumn(colDef) {
        const colInfo = DicVal(colDef, "colinfo", {});
        return String(
            DicVal(colInfo, "dbcolumn",
                DicVal(colInfo, "dbcolumnnm",
                    DicVal(colDef, "dbcolumn", DicVal(colDef, "dbcolumnnm", ""))
                )
            ) ?? ""
        ).trim();
    }

    function resolveKeyInfoDefinition(colDef) {
        const colInfo = DicVal(colDef, "colinfo", {});
        return String(DicVal(colInfo, "keyinfo", DicVal(colDef, "keyinfo", "")) ?? "").trim();
    }

    function isDedicatedKakousijiMasterColumn(colDef) {
        const fieldId = normalizeColumnIdUpper(resolveFieldId(colDef));
        return fieldId === "SSDM_COLORCD"
            || fieldId === "SSDSH_COLORCD"
            || fieldId === "SSDSH_COLORNO";
    }

    function isMasterColumn(colDef) {
        if (isEmpty(colDef)) return false;
        if (isDedicatedKakousijiMasterColumn(colDef)) return false;
        if (resolveDatatype(colDef) !== CELLTYPE.MASTER) return false;
        return resolveFieldId(colDef) !== ""
            && resolveDataId(colDef) !== ""
            && resolveDbColumn(colDef) !== "";
    }

    function resolveMasterColumns(panelInfo) {
        const defs = resolvePanelMasterColumnDefs(panelInfo);
        if (!Array.isArray(defs) || defs.length === 0) return [];
        return defs.filter((colDef) => isMasterColumn(colDef));
    }

    function readMasterCode(row, fieldId) {
        const code = normalizeText(
            readRowValueIgnoreCase(row, `${fieldId}_cd`)
            || readRowValueIgnoreCase(row, `${fieldId}_CD`)
            || readRowValueIgnoreCase(row, fieldId)
        );
        return extractMasterCodeFromDisplayValue(code);
    }

    function readMasterName(row, fieldId, dbcolumn) {
        const direct = normalizeText(
            readRowValueIgnoreCase(row, `${fieldId}_nm`)
            || readRowValueIgnoreCase(row, `${fieldId}_NM`)
            || readRowValueIgnoreCase(row, dbcolumn)
        );
        if (direct !== "") return direct;
        return extractMasterNameFromDisplayValue(readRowValueIgnoreCase(row, fieldId));
    }

    function buildStoreMasterRow(dataid, keyinfo, code, dbcolumn, name) {
        const row = { dataid, code };
        if (keyinfo !== "") row.keyinfo = keyinfo;
        row[dbcolumn] = name;
        return row;
    }

    function primeMasterStore(dataid, keyinfo, code, dbcolumn, name) {
        if (dataid === "" || code === "" || dbcolumn === "" || name === "") return;
        if (isEmpty(fnc) || isEmpty(fnc.current) || typeof fnc.current.setMaster !== "function") return;
        fnc.current.setMaster(buildStoreMasterRow(dataid, keyinfo, code, dbcolumn, name));
    }

    function resolveKeyInfoValue(colDef, row) {
        const keyinfoDef = resolveKeyInfoDefinition(colDef);
        const dataid = resolveDataId(colDef);
        if (keyinfoDef === "") return { dataid, keyinfo: "" };
        if (dataid.indexOf("d_slip") !== -1) {
            const key = String(keyinfoDef.split("=")[1] ?? "").trim();
            const rowKey = String(key.split(":")[1] ?? "").trim();
            const nextDataid = dataid.replace("d_slip", "d_prod");
            const keyValue = normalizeText(readRowValueIgnoreCase(row, rowKey));
            return {
                dataid: nextDataid,
                keyinfo: keyValue === "" ? "" : `${nextDataid}_key=${keyValue}`
            };
        }
        const left = String(keyinfoDef.split("=")[0] ?? "").trim();
        const right = String(keyinfoDef.split("=")[1] ?? "").trim();
        const keyValue = normalizeText(readRowValueIgnoreCase(row, right));
        return {
            dataid,
            keyinfo: left === "" || keyValue === "" ? "" : `${left}=${keyValue}`
        };
    }

    function pickNameFromMasterRow(masterRow, fieldId, dbcolumn) {
        return normalizeText(
            readRowValueIgnoreCase(masterRow, dbcolumn)
            || readRowValueIgnoreCase(masterRow, `${fieldId}_nm`)
            || readRowValueIgnoreCase(masterRow, `${fieldId}_NM`)
            || readRowValueIgnoreCase(masterRow, "name")
            || readRowValueIgnoreCase(masterRow, "label")
        );
    }

    function readNameFromStore(colDef, row, code) {
        if (code === "") return "";
        const fieldId = resolveFieldId(colDef);
        const dbcolumn = resolveDbColumn(colDef);
        const request = resolveKeyInfoValue(colDef, row);
        if (isEmpty(fnc) || isEmpty(fnc.current) || typeof fnc.current.getMaster !== "function") return "";

        let masterRow = fnc.current.getMaster(request.dataid, code, request.keyinfo);
        if (isEmpty(masterRow) && request.keyinfo !== "") {
            masterRow = fnc.current.getMaster(request.dataid, code);
        }
        if (isEmpty(masterRow)) return "";

        const name = pickNameFromMasterRow(masterRow, fieldId, dbcolumn);
        if (name !== "") {
            primeMasterStore(request.dataid, request.keyinfo, code, dbcolumn, name);
        }
        return name;
    }

    function writeResolvedMasterDisplay(row, fieldId, code, name) {
        if (isEmpty(row) || typeof row !== "object") return row;
        const next = { ...row };
        const rawValue = normalizeText(readRowValueIgnoreCase(next, fieldId));
        if (code !== "" && rawValue !== "" && rawValue !== code && rawValue.indexOf(":") !== -1) {
            next[fieldId] = code;
        }
        if (code !== "") {
            next[`${fieldId}_cd`] = code;
            next[`${fieldId}_CD`] = code;
        }
        if (name !== "") {
            next[`${fieldId}_nm`] = name;
            next[`${fieldId}_NM`] = name;
        }
        return next;
    }

    function hydrateRowsSyncDetailed(panelInfo, rows) {
        const sourceRows = Array.isArray(rows) ? rows : [];
        const masterColumns = resolveMasterColumns(panelInfo);
        if (masterColumns.length === 0 || sourceRows.length === 0) {
            return { rows: sourceRows, changed: false };
        }

        let changed = false;
        const nextRows = sourceRows.map((row) => {
            if (isEmpty(row) || typeof row !== "object") return row;
            let nextRow = row;
            masterColumns.forEach((colDef) => {
                const fieldId = resolveFieldId(colDef);
                const dbcolumn = resolveDbColumn(colDef);
                const request = resolveKeyInfoValue(colDef, nextRow);
                const code = readMasterCode(nextRow, fieldId);
                let name = readMasterName(nextRow, fieldId, dbcolumn);
                if (code === "") return;
                if (name === "") {
                    name = readNameFromStore(colDef, nextRow, code);
                } else {
                    primeMasterStore(request.dataid, request.keyinfo, code, dbcolumn, name);
                }
                if (name === "") return;
                const existingCode = normalizeText(readRowValueIgnoreCase(nextRow, `${fieldId}_cd`));
                const existingName = normalizeText(readRowValueIgnoreCase(nextRow, `${fieldId}_nm`));
                const rawValue = normalizeText(readRowValueIgnoreCase(nextRow, fieldId));
                const needsDisplayFix = rawValue !== "" && rawValue !== code && rawValue.indexOf(":") !== -1;
                if (existingCode === code && existingName === name && !needsDisplayFix) return;
                nextRow = writeResolvedMasterDisplay(nextRow, fieldId, code, name);
                changed = true;
            });
            return nextRow;
        });

        return { rows: changed ? nextRows : sourceRows, changed };
    }

    function hydrateRowsForDisplay(panelInfo, rows) {
        return hydrateRowsSyncDetailed(panelInfo, rows).rows;
    }

    function buildPendingKey(panelInfo, row, rowIndex, fieldId, dataid, keyinfo, code) {
        const panelType = normalizeText(DicVal(panelInfo, "type", ""));
        const rowId = normalizeText(readRowValueIgnoreCase(row, "rowid")) || String(rowIndex);
        return [panelType, rowId, fieldId, dataid, keyinfo, code].join("|");
    }

    function applyResolvedNameToPanel(panelInfo, row, rowIndex, fieldId, code, name) {
        const currentRows = getGridRows(panelInfo);
        if (!Array.isArray(currentRows) || currentRows.length === 0) return false;
        const targetRowId = normalizeText(readRowValueIgnoreCase(row, "rowid"));
        let targetIndex = -1;
        if (targetRowId !== "") {
            targetIndex = currentRows.findIndex((item) => normalizeText(readRowValueIgnoreCase(item, "rowid")) === targetRowId);
        }
        if (targetIndex === -1 && Number.isInteger(rowIndex) && rowIndex >= 0 && rowIndex < currentRows.length) {
            targetIndex = rowIndex;
        }
        if (targetIndex === -1) return false;

        const currentRow = currentRows[targetIndex];
        if (readMasterCode(currentRow, fieldId) !== code) return false;
        const nextRow = writeResolvedMasterDisplay(currentRow, fieldId, code, name);
        if (nextRow === currentRow) return false;
        const nextRows = [...currentRows];
        nextRows[targetIndex] = nextRow;
        setGridRows(panelInfo, nextRows);
        if (isGridReady(panelInfo)) {
            applyGridRowsNow(panelInfo, nextRows);
        }
        return true;
    }

    function requestMissingNames(panelInfo, rows, masterColumns) {
        if (!Array.isArray(rows) || rows.length === 0 || !Array.isArray(masterColumns) || masterColumns.length === 0) return;
        if (isEmpty(fnc) || isEmpty(fnc.current) || typeof fnc.current.getMasterdata !== "function") return;

        rows.forEach((row, rowIndex) => {
            if (isEmpty(row) || typeof row !== "object") return;
            masterColumns.forEach((colDef) => {
                const fieldId = resolveFieldId(colDef);
                const dbcolumn = resolveDbColumn(colDef);
                const request = resolveKeyInfoValue(colDef, row);
                const code = readMasterCode(row, fieldId);
                const name = readMasterName(row, fieldId, dbcolumn) || readNameFromStore(colDef, row, code);
                if (code === "" || name !== "") return;

                const pendingKey = buildPendingKey(panelInfo, row, rowIndex, fieldId, request.dataid, request.keyinfo, code);
                if (DicVal(masterNameHydrationPendingRef.current, pendingKey, false) === true) return;
                masterNameHydrationPendingRef.current[pendingKey] = true;

                const rowdata = {
                    ...(isEmpty(row) ? {} : row),
                    ...(isEmpty(entrydataRef) || isEmpty(entrydataRef.current) ? {} : entrydataRef.current)
                };
                const others = [{ columnid: fieldId, dbcolumn }];

                fnc.current.getMasterdata(
                    request.dataid,
                    code,
                    request.keyinfo,
                    others,
                    rowdata,
                    (data) => {
                        delete masterNameHydrationPendingRef.current[pendingKey];
                        if (!isEmpty(data) && typeof fnc.current.setMaster === "function") {
                            fnc.current.setMaster(data);
                        }
                        const resolvedName = pickNameFromMasterRow(data, fieldId, dbcolumn);
                        if (resolvedName === "") return;
                        primeMasterStore(request.dataid, request.keyinfo, code, dbcolumn, resolvedName);
                        applyResolvedNameToPanel(panelInfo, row, rowIndex, fieldId, code, resolvedName);
                    },
                    // 名称補完は行×マスタ列で多重に走る裏側処理のため、ローディングは表示しない。
                    // 表示する(true)と postaxios が wait(true) を出す一方、上記コールバックは
                    // wait(false) を呼ばないためオーバーレイが残る。他の名称補完も表示なしで統一。
                    false
                );
            });
        });
    }

    function hydratePanelRowsAsync(panelInfo, rowsOverride = undefined) {
        if (isEmpty(panelInfo)) return;
        const currentRows = Array.isArray(rowsOverride) ? rowsOverride : getGridRows(panelInfo);
        const syncResult = hydrateRowsSyncDetailed(panelInfo, currentRows);
        const rows = syncResult.rows;
        if (syncResult.changed) {
            setGridRows(panelInfo, rows);
            if (isGridReady(panelInfo)) {
                applyGridRowsNow(panelInfo, rows);
            }
        }
        requestMissingNames(panelInfo, rows, resolveMasterColumns(panelInfo));
    }

    function hydrateAllGridRowsAsync() {
        const panels = Array.isArray(getGridList()) ? getGridList() : [];
        panels.forEach((panelInfo) => hydratePanelRowsAsync(panelInfo));
    }

    return {
        hydrateRowsForDisplay,
        hydratePanelRowsAsync,
        hydrateAllGridRowsAsync
    };
}
